import React from 'react';
import { Box, Typography, ButtonBase, Stack, Chip } from '@mui/material';
import {
  Description as SoapIcon,
  Medication as MedIcon,
  Vaccines as VaxIcon,
  Healing as ProcIcon,
  Note as VisitIcon,
  PlayArrow as ResumeIcon,
  Add as AddIcon,
  Phone as CommIcon,
  TrendingUp as UpcomingIcon,
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import { SoapNote } from '../../../types/soap';

export interface QuickActionStripProps {
  draftSoap: SoapNote | null;
  overdueVaccineCount: number;
  pendingRefillCount: number;
  onResumeDraft: (note: SoapNote) => void;
  onNewSoap: () => void;
  onNewVisitNote: () => void;
  onAddMedication: () => void;
  onAddVaccination: () => void;
  onAddProcedure: () => void;
  onLogCommunication: () => void;
  canCreateSoap: boolean;
  /** `manage:patients` — the PATIENT edits. Nothing on this strip renders from it today; kept
   *  because the strip's caller passes both flags and the next non-clinical card belongs to it. */
  canEdit: boolean;
  /**
   * `manage:medical_records` — the CLINICAL adds (P5, Ali default #64). Every card below that
   * opens a clinical dialog POSTs to a route requiring that slug (/medications, /vaccinations,
   * /procedures, /medical-records, /client-communications), so rendering them from `canEdit`
   * (`manage:patients`) offered a CSR five buttons whose save 403s. All four legacy roles hold
   * `manage:medical_records`, so nobody loses a card.
   */
  canAddClinical: boolean;
}

const ActionCard: React.FC<{
  onClick: () => void;
  icon: React.ReactNode;
  primary: React.ReactNode;
  secondary?: React.ReactNode;
  variant?: 'primary' | 'accent' | 'ghost';
  testId?: string;
}> = ({ onClick, icon, primary, secondary, variant = 'ghost', testId }) => {
  const isPrimary = variant === 'primary';
  const isAccent = variant === 'accent';
  return (
    <ButtonBase
      onClick={onClick}
      data-testid={testId}
      sx={{
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        px: 2,
        py: 1.25,
        borderRadius: 2.5,
        textAlign: 'left',
        transition: 'all 160ms cubic-bezier(0.4, 0, 0.2, 1)',
        ...(isPrimary && {
          bgcolor: '#C9772B',
          color: '#fff',
          boxShadow: '0 2px 8px rgba(201,119,43,0.35)',
          '&:hover': { bgcolor: '#A8631F', transform: 'translateY(-1px)', boxShadow: '0 4px 14px rgba(201,119,43,0.45)' },
        }),
        ...(isAccent && {
          bgcolor: '#fff',
          color: '#3B2E1A',
          border: '1px solid',
          borderColor: '#C9772B',
          boxShadow: '0 2px 6px rgba(0,0,0,0.04)',
          '&:hover': { bgcolor: '#FFF8F1', transform: 'translateY(-1px)', boxShadow: '0 4px 12px rgba(201,119,43,0.2)' },
        }),
        ...(variant === 'ghost' && {
          bgcolor: 'transparent',
          color: '#3B2E1A',
          border: '1px solid',
          borderColor: 'divider',
          '&:hover': { bgcolor: 'rgba(59,46,26,0.04)', borderColor: '#A0744D' },
        }),
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 32,
          height: 32,
          borderRadius: '50%',
          bgcolor: isPrimary ? 'rgba(255,255,255,0.2)' : isAccent ? 'rgba(201,119,43,0.1)' : 'rgba(59,46,26,0.06)',
        }}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            fontSize: '0.85rem',
            lineHeight: 1.2,
            color: 'inherit',
            whiteSpace: 'nowrap',
          }}
        >
          {primary}
        </Typography>
        {secondary && (
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              fontSize: '0.7rem',
              lineHeight: 1.2,
              color: 'inherit',
              opacity: 0.75,
              mt: 0.25,
              whiteSpace: 'nowrap',
            }}
          >
            {secondary}
          </Typography>
        )}
      </Box>
    </ButtonBase>
  );
};

/**
 * Action-first strip at the top of the chart. Anticipates what the vet is
 * here to do: resume a draft SOAP if one exists, otherwise lead with "+ New
 * SOAP". Contextual chips for overdue vaccines or pending refill requests
 * surface time-sensitive work without the vet having to look for it.
 *
 * The primary action gets visual weight (filled, shadow); supporting
 * actions sit alongside as outlined cards so the eye lands on the right
 * thing first.
 */
const QuickActionStrip: React.FC<QuickActionStripProps> = ({
  draftSoap,
  overdueVaccineCount,
  pendingRefillCount,
  onResumeDraft,
  onNewSoap,
  onNewVisitNote,
  onAddMedication,
  onAddVaccination,
  onAddProcedure,
  onLogCommunication,
  canCreateSoap,
  canAddClinical,
}) => {
  // LEFT AS IS ON PURPOSE — DATE-KINDS.md §2.3 carve-out. `formatDistanceToNow` diffs
  // two instants ("3 hours ago"), so it has no zone to get wrong: shifting the input into
  // the clinic's zone would shift the *reference* moment too and change nothing, and
  // date-parting it would throw away the time this render exists to show. The ratchet
  // (scripts/check-date-idioms.js) matches it only because the regex is `format[A-Za-z]*(
  // new Date(` — it cannot tell a distance from a wall-clock render. Both inputs are
  // instants either way: `SoapNote.lastAutosavedAt`/`updatedAt` are `@updatedAt`, and
  // `SoapNote.date` is an instant in every arm (§1.2's last row).
  const draftAge = draftSoap ? formatDistanceToNow(new Date(draftSoap.updatedAt || draftSoap.date), { addSuffix: true }) : null;
  const draftTitle = draftSoap?.assessment?.primaryDiagnosis || draftSoap?.templateUsed || 'SOAP draft';

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        flexWrap: 'wrap',
        py: 1,
      }}
    >
      {/* Lead with the resume action when a draft exists — it's almost
          always what the vet came back to do. */}
      {draftSoap && (
        <ActionCard
          onClick={() => onResumeDraft(draftSoap)}
          variant="primary"
          icon={<ResumeIcon sx={{ fontSize: 18 }} />}
          primary={`Resume "${draftTitle.slice(0, 36)}${draftTitle.length > 36 ? '…' : ''}"`}
          secondary={`Draft saved ${draftAge}`}
          testId="qa-resume-draft"
        />
      )}

      {canCreateSoap && (
        <ActionCard
          onClick={onNewSoap}
          variant={draftSoap ? 'accent' : 'primary'}
          icon={<SoapIcon sx={{ fontSize: 18 }} />}
          primary="New SOAP Note"
          secondary={draftSoap ? undefined : 'Start a new exam'}
          testId="qa-new-soap"
        />
      )}

      {canAddClinical && (
        <ActionCard
          onClick={onAddMedication}
          variant="ghost"
          icon={<MedIcon sx={{ fontSize: 18 }} />}
          primary="Add Medication"
          secondary={pendingRefillCount > 0 ? `${pendingRefillCount} refill request${pendingRefillCount === 1 ? '' : 's'}` : undefined}
          testId="qa-add-med"
        />
      )}

      {canAddClinical && (
        <ActionCard
          onClick={onAddVaccination}
          variant="ghost"
          icon={<VaxIcon sx={{ fontSize: 18 }} />}
          primary="Add Vaccine"
          secondary={overdueVaccineCount > 0 ? `${overdueVaccineCount} overdue` : undefined}
          testId="qa-add-vax"
        />
      )}

      {canAddClinical && (
        <ActionCard
          onClick={onAddProcedure}
          variant="ghost"
          icon={<ProcIcon sx={{ fontSize: 18 }} />}
          primary="Procedure"
          testId="qa-add-proc"
        />
      )}

      {canAddClinical && (
        <ActionCard
          onClick={onNewVisitNote}
          variant="ghost"
          icon={<VisitIcon sx={{ fontSize: 18 }} />}
          primary="Visit Note"
          testId="qa-visit-note"
        />
      )}

      {canAddClinical && (
        <ActionCard
          onClick={onLogCommunication}
          variant="ghost"
          icon={<CommIcon sx={{ fontSize: 18 }} />}
          primary="Log Call"
          testId="qa-log-call"
        />
      )}

      {/* Right-side contextual badges for items needing attention */}
      <Box sx={{ flexGrow: 1 }} />

      {(overdueVaccineCount > 0 || pendingRefillCount > 0) && (
        <Stack direction="row" spacing={0.75} sx={{ ml: 'auto' }}>
          <UpcomingIcon sx={{ fontSize: 16, color: 'warning.main', alignSelf: 'center' }} />
          {overdueVaccineCount > 0 && (
            <Chip
              size="small"
              label={`${overdueVaccineCount} vax overdue`}
              color="warning"
              variant="outlined"
              sx={{ height: 24, fontSize: '0.7rem', fontWeight: 600 }}
            />
          )}
          {pendingRefillCount > 0 && (
            <Chip
              size="small"
              label={`${pendingRefillCount} refill${pendingRefillCount === 1 ? '' : 's'} pending`}
              color="info"
              variant="outlined"
              sx={{ height: 24, fontSize: '0.7rem', fontWeight: 600 }}
            />
          )}
        </Stack>
      )}
    </Box>
  );
};

// Suppress unused import warning for AddIcon (kept available for future actions)
void AddIcon;

export default QuickActionStrip;
